import { IS_DEV_ENV } from "constants/app.constants";
import { EntityType } from "constants/entities.enum";
import { createEntityState } from "entities/charts/factory";
import { createMergeWithDefaults } from "entities/utils";
import { EvaChartFeatures } from "types";

import {
  DEFAULT_MIDSTREAM_CHART_TYPE_PARAM_X_PROPERTIES,
  DEFAULT_MIDSTREAM_CHART_TYPE_PARAM_Y_PROPERTIES
} from "../../../../constants";
import {
  DEFAULT_BOX_PLOT_BINS_FEATURE_ENTITY,
  DEFAULT_HIGHLIGHTED_LINE_OPACITY_FEATURE_ENTITY,
  DEFAULT_HIGHLIGHTED_LINE_SIZE_FEATURE_ENTITY,
  DEFAULT_LINE_OPACITY_FEATURE_ENTITY,
  DEFAULT_LINE_SIZE_FEATURE_ENTITY
} from "./feature";

const BOX_PLOT_MIDSTREAM_FEATURES: Partial<EvaChartFeatures> = {
  screenshot: createEntityState(EntityType.ScreenshotFeature, {
    show: true,
    enabled: true
  }),
  fullScreen: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true
  }),
  datatable: createEntityState(EntityType.ChartDataTableFeature, {
    show: true,
    enabled: true
  }),
  spaghetti: createEntityState(EntityType.ChartsFeature, { show: true }),
  debug: createEntityState(EntityType.ChartsFeature, {
    show: IS_DEV_ENV,
    enabled: IS_DEV_ENV
  }),
  cutoff: createEntityState(EntityType.CutOffChartFeature, {
    show: true,
    enabled: true
  }),
  shutInMonths: createEntityState(EntityType.ShutInMonthsChartFeature, {
    show: true,
    enabled: true
  }),
  movingAverageDays: createEntityState(EntityType.MovingAverageDaysChartFeature, {
    show: true,
    enabled: true
  }),
  survivorBias: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true,
    active: true
  }),
  useWeightedRatioAverage: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true,
    active: true
  }),
  lockUnits: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true
  }),
  lasso: createEntityState(EntityType.LassoFeature, {
    show: true,
    enabled: true
  }),
  pointSize: createEntityState(EntityType.PointSizeFeature, {
    show: true,
    enabled: true
  }),
  pointOpacity: createEntityState(EntityType.PointOpacityFeature, {
    show: true,
    enabled: true
  }),
  lineSize: {
    ...DEFAULT_LINE_SIZE_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  lineOpacity: {
    ...DEFAULT_LINE_OPACITY_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  highlightedLineSize: {
    ...DEFAULT_HIGHLIGHTED_LINE_SIZE_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  highlightedLineOpacity: {
    ...DEFAULT_HIGHLIGHTED_LINE_OPACITY_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  useSeriesStyleOverride: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: false, // Only enabled when jitter plot is active
    active: false
  }),
  chartFocus: createEntityState(EntityType.ChartFocusMidStreamFeature, {
    show: true,
    enabled: true
  }),
  legend: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true
  }),
  jitterPlotToggle: createEntityState(EntityType.JitterPlotToggleFeature, {
    show: true,
    enabled: true,
    properties: {
      jitterBin: null,
      xBinDefaulting: false
    }
  }),
  violinPlotToggle: createEntityState(EntityType.ViolinPlotToggleFeature, {
    show: true,
    enabled: true
  }),
  boxPlotBins: DEFAULT_BOX_PLOT_BINS_FEATURE_ENTITY,
  dataRangeSelect: createEntityState(EntityType.DataRangeSelectFeature, {
    show: true,
    enabled: true
  }),
  chartTypeParam: createEntityState(EntityType.ChartTypeParamFeature, {
    properties: {
      // box plot takes x field from chart focus since there is no field selector, defaulted for
      // default x so field can transfer from cross plot to box plot and back
      x: DEFAULT_MIDSTREAM_CHART_TYPE_PARAM_X_PROPERTIES,
      y: DEFAULT_MIDSTREAM_CHART_TYPE_PARAM_Y_PROPERTIES
    }
  }),
  bestFitToggle: createEntityState(EntityType.BestFitToggleFeature, {}),
  histogramToggle: createEntityState(EntityType.HistogramToggleFeature, {}),
  quarterSlope: createEntityState(EntityType.SlopeLineFeature, {}),
  unitSlope: createEntityState(EntityType.SlopeLineFeature, {}),
  halfSlope: createEntityState(EntityType.SlopeLineFeature, {}),
  useProbitLineOfBestFitData: createEntityState(EntityType.ChartsFeature, {}),
  xAxisFieldSync: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true,
    active: false
  }),
  // Without this the facility box plot inherits DEFAULT_X_AXIS_SORT_ENTITY ("desc") and
  // the _has guard drops every click on the control it already shows.
  xAxisSort: createEntityState(EntityType.XAxisSortFeature, {
    properties: {
      value: ""
    }
  })
};

export const boxPlotMidstreamFeatures = createMergeWithDefaults(
  BOX_PLOT_MIDSTREAM_FEATURES
);
